import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useForm, FormProvider } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import { Alert, Box, DialogActions, DialogContent, Fab, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { accountStore } from 'features/account/stores/account.slice';
import { phoneRegex } from 'features/account/config/account-form.const';
import FormInput from 'components/FormInput';
import Grid from '@mui/material/Grid';
import {
   postSubscriptionRequestAsync,
   resetPostSubscriptionRequestStatus,
   subscriptionStore,
} from '../stores/subscriptions.slice';
import FormButton from 'components/FormButton';
import { LoadStatus } from 'config/utils';
import ConditionalRender from 'components/ConditionalRender';

interface IRequestChangeFormValues {
   firstName: string;
   lastName: string;
   email: string;
   phone: string;
}

const REQUEST_CHANGE_SCHEMA = yup.object({
   firstName: yup.string().required('First name is required'),
   lastName: yup.string().required('Last name is required'),
   email: yup.string().email('Email format is not valid').required('Email is required'),
   phone: yup.string().matches(phoneRegex, 'Invalid phone number').required('Phone number is required'),
});

interface IRequestChangeProps {
   requestChangeOpen: boolean;
   setRequestChangeOpen: (open: boolean) => void;
}

const SubscriptionRequestDialog: React.FC<IRequestChangeProps> = ({ requestChangeOpen, setRequestChangeOpen }) => {
   const dispatch = useAppDispatch();
   const { user } = useAppSelector(accountStore);
   const { postSubscriptionRequestStatus, subscriptionRequestType } = useAppSelector(subscriptionStore);
   const theme = useTheme();
   const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
   const [showSuccess, setShowSuccess] = useState(false);
   const isLoading = postSubscriptionRequestStatus === LoadStatus.loading;

   const methods = useForm<IRequestChangeFormValues>({
      defaultValues: {
         firstName: user.firstName,
         lastName: user.lastName,
         email: user.email,
         phone: user.phone,
      },
      resolver: yupResolver(REQUEST_CHANGE_SCHEMA),
   });

   const handleClose = () => {
      methods.reset();
      dispatch(resetPostSubscriptionRequestStatus());
      setRequestChangeOpen(false);
      setShowSuccess(false);
   };

   const onSubmit = (data: any) => {
      dispatch(
         postSubscriptionRequestAsync({
            bodyInput: {
               ...data,
               subject: `Subscription ${subscriptionRequestType.toString()} Request`,
               content: `Subscription ${subscriptionRequestType.toString()} Request`,
            },
         }),
      );
   };

   useEffect(
      function onPostSubscriptionRequestStatus() {
         if (postSubscriptionRequestStatus === LoadStatus.complete) {
            dispatch(resetPostSubscriptionRequestStatus());
            setShowSuccess(true);
         }
      },
      [postSubscriptionRequestStatus],
   );

   return (
      <Dialog
         className="base-dialog"
         fullScreen={isMobileScreen}
         disableScrollLock
         onClose={handleClose}
         open={requestChangeOpen}
         PaperProps={{
            className: 'base-style',
         }}
      >
         <div className="base-dialog-header">
            <DialogTitle className={'title'}>{`${subscriptionRequestType.toString()} Request`}</DialogTitle>
            <Fab size="small" color="primary" onClick={handleClose}>
               <CloseIcon />
            </Fab>
         </div>
         <DialogContent className="base-dialog-content">
            <div className="fields-container">
               <ConditionalRender
                  condition={!showSuccess}
                  componentIfTrue={
                     <FormProvider {...methods}>
                        <form id="account-popup" onSubmit={methods.handleSubmit(onSubmit)}>
                           <Box my={2} sx={{ p: 0 }}>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <FormInput name="firstName" label="First Name" autoComplete="given-name" />
                                 </Grid>
                                 <Grid item xs={12}>
                                    <FormInput name="lastName" label="Last Name" autoComplete="family-name" />
                                 </Grid>
                                 <Grid item xs={12}>
                                    <FormInput name="email" label="Email" autoComplete="email" />
                                 </Grid>

                                 <Grid item xs={12}>
                                    <FormInput name="phone" label="Phone" type={'tel'} autoComplete={'tel-national'} />
                                 </Grid>
                              </Grid>
                           </Box>
                        </form>
                     </FormProvider>
                  }
                  componentIfFalse={
                     <Alert sx={{ width: '100%' }} severity="success">
                        Request sent. Please allow for 24-48 hours for a response.
                     </Alert>
                  }
               />
            </div>
         </DialogContent>
         <DialogActions className="base-dialog-footer">
            <ConditionalRender
               condition={!showSuccess}
               componentIfTrue={
                  <FormButton
                     label={`Request ${subscriptionRequestType.toString()}`}
                     isLoading={isLoading}
                     onClick={methods.handleSubmit(onSubmit)}
                  />
               }
               componentIfFalse={<FormButton label="Close" isLoading={isLoading} onClick={handleClose} />}
            />
         </DialogActions>
      </Dialog>
   );
};

export default SubscriptionRequestDialog;
